/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { ExtractedErrorText } from '@app/core/types/index';
import { NodeId } from '@shared/types/node';
import { QueryField, SystemView } from '@shared/types/queries';
import { QueryField3x, SystemView3x } from '@shared/types/queries-3x';

export const SystemViewTuple = [...Object.values(SystemView)] as const;
export type SystemViewType = (typeof SystemViewTuple)[number];

export const SystemViewTuple3x = [...Object.values(SystemView3x)] as const;
export type SystemViewType3x = (typeof SystemViewTuple3x)[number];

export type SqlQueryString = string;

export const LimitRows = [1, 5, 15, 25, 50, 100, 200, 500, 1000] as const;
export type LimitRowsType = (typeof LimitRows)[number];

// update interval for query updates in minutes
export const UpdateIntervals = [0.0833333333, 0.5, 1, 5, 15, 30, 60, 180] as const;
export type UpdateIntervalType = (typeof UpdateIntervals)[number];

export enum QueryWidgetType {
  'SYSTEM_VIEW' = 'SYSTEM_VIEW',
  'SQL' = 'SQL',
}

export type QueryParams = {
  limitRows: LimitRowsType;
  nodeConsistentId: NodeId | null;
  updateInterval: UpdateIntervalType;
};

export type QueryWidgetSettings<T extends QueryWidgetType.SYSTEM_VIEW | QueryWidgetType.SQL> = {
  queryWidgetType: T;
  params: QueryParams & {
    query: T extends QueryWidgetType.SYSTEM_VIEW ? SystemViewType | SystemViewType3x | null : SqlQueryString;
  };
};

export enum QueryErrorType {
  'REQUEST_FAILED' = 'REQUEST_FAILED',
  'NOT_AUTHORIZED' = 'NOT_AUTHORIZED',
}

export type QueryResponse = {
  columns: QueryField[] | QueryField3x[];
  rows: (number | string | boolean)[][];
  hasMore: boolean;
  failed: boolean;
} & (
  | {
      errorType: undefined;
      error: undefined;
    }
  | {
      errorType: QueryErrorType;
      error: ExtractedErrorText;
    }
);
